import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { AtSign, ClipboardList, Crown, ListOrdered, Lock, Ticket, X } from 'lucide-react';
import { Fragment, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDate } from '../lib/format';
import { MAX_TAGS, TAGGABLE_KEY, sameTag, tagKindKey, tagLink, type NoteTag, type TaggableOptions } from '../lib/notes';
import { fold } from './PeoplePicker';
import { statusTone, type BadgeTone } from './ui';

/**
 * Tags on My notes (operator, 2026-10-15: "i must can tag @ task or ticket from
 * the list who assigned to me"): the field a line or a note is written in, where
 * @ opens the reader's own open work to pick from, and the chips the picked
 * tags show as.
 */

/** A tag's status as a dot, in the colour the rest of the console gives it. */
const STATUS_DOT: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-500',
  brand: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
  info: 'bg-info',
  plasma: 'bg-ai-plasma',
};

function TagIcon({ tag, className }: { tag: Pick<NoteTag, 'kind' | 'isLeaderTask'>; className?: string }) {
  const Icon = tag.kind === 'TICKET' ? Ticket : tag.kind === 'Q_TASK' ? ListOrdered : tag.isLeaderTask ? Crown : ClipboardList;
  return <Icon className={clsx('h-3.5 w-3.5 shrink-0', className)} aria-hidden />;
}

const CHIP =
  'inline-flex max-w-full items-center gap-1.5 rounded-pill border bg-[var(--surface-raised)] py-0.5 ps-2 text-caption text-[var(--text-primary)]';

/**
 * A tag: what it is, its number, its name and its status — a link to it in a
 * list, or with a × to take it off while a line or a note is being written.
 * A record its owner can no longer open shows as no longer available and says
 * nothing more; one no longer given to them has a dashed edge.
 */
export function TagChip({ tag, onRemove }: { tag: NoteTag; onRemove?: () => void }) {
  const { t, locale } = useI18n();
  const kind = t(tagKindKey(tag));
  const remove = onRemove && (
    <button
      type="button"
      onClick={onRemove}
      className="shrink-0 rounded-full p-0.5 text-[var(--text-tertiary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
      aria-label={t('notes.tag.remove')}
      title={t('notes.tag.remove')}
    >
      <X className="h-3.5 w-3.5" aria-hidden />
    </button>
  );

  if (!tag.available) {
    return (
      <span className={clsx(CHIP, 'hairline pe-2 text-faint', onRemove && 'pe-1')} title={t('notes.tag.unavailableHint')}>
        <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">{t('notes.tag.unavailable', { kind })}</span>
        {remove}
      </span>
    );
  }

  const status = enumLabel(tag.status, locale);
  const tooltip = [
    [kind, tag.number, tag.title].filter(Boolean).join(' · '),
    status,
    tag.mine === false ? t('notes.tag.notYours') : null,
  ]
    .filter(Boolean)
    .join(' — ');
  const body = (
    <>
      <TagIcon tag={tag} className="text-faint" />
      <span className="sr-only">{kind}: </span>
      {tag.number && (
        <span className="shrink-0 font-mono text-[11px] text-muted" dir="ltr">
          {tag.number}
        </span>
      )}
      <bdi className="min-w-0 truncate">{tag.title}</bdi>
      <span className={clsx('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT[statusTone(tag.status)])} aria-hidden />
      <span className="shrink-0 text-[11px] text-muted">{status}</span>
    </>
  );
  const edge = tag.mine === false ? 'border-dashed border-[var(--line-strong)]' : 'hairline';

  if (onRemove) {
    return (
      <span className={clsx(CHIP, edge, 'pe-1')} title={tooltip}>
        {body}
        {remove}
      </span>
    );
  }
  return (
    <Link
      to={tagLink(tag)}
      title={tooltip}
      className={clsx(CHIP, edge, 'pe-2 transition-colors duration-fast hover:border-primary/60 hover:text-primary-600 dark:hover:text-primary-400')}
    >
      {body}
    </Link>
  );
}

/** A line's or a note's tags, in a row that wraps. */
export function TagChips({
  tags,
  onRemove,
  className,
}: {
  tags: NoteTag[];
  onRemove?: (tag: NoteTag) => void;
  className?: string;
}) {
  if (tags.length === 0) return null;
  return (
    <div className={clsx('flex min-w-0 flex-wrap items-center gap-1.5', className)}>
      {tags.map((tag) => (
        <TagChip key={`${tag.kind}:${tag.id}`} tag={tag} onRemove={onRemove ? () => onRemove(tag) : undefined} />
      ))}
    </div>
  );
}

/** The longest @ query looked back for — past it, the @ is just a character. */
const MAX_QUERY = 40;
/** As many matches as the menu lists; it scrolls. */
const SHOWN = 50;
/** How long the list of the reader's open work counts as fresh when a new @ is typed. */
const FRESH_FOR = 5_000;

/**
 * The @ being typed: one at the start or after a space (so an email address
 * never opens the menu), on the caret's line, with what follows it up to the
 * caret as the query — spaces included, since names have them.
 */
export function findTrigger(text: string, caret: number | null): { at: number; query: string } | null {
  if (caret === null) return null;
  for (let i = caret - 1; i >= Math.max(0, caret - MAX_QUERY - 1); i--) {
    const ch = text[i];
    if (ch === '\n') return null;
    if (ch === '@') {
      if (i > 0 && !/\s/.test(text[i - 1])) return null;
      return { at: i, query: text.slice(i + 1, caret) };
    }
  }
  return null;
}

/** The @ being typed: where it is — an offset, or a place in a document — and what follows it. */
export interface TagTrigger {
  at: number;
  query: string;
}

/**
 * The @ menu of a field tags are typed in — a line's input, the note editor:
 * the reader's open work narrowed by what follows the @ (by number, name, kind
 * or status, in either language), which option is highlighted, and the keys
 * that move through it. The field says where its @ is and does the picking.
 */
export function useTagMenu({
  trigger,
  focused,
  tags,
}: {
  /** The @ at the caret, or null — none, or the field not in use. */
  trigger: TagTrigger | null;
  /** The field is in use: the reader's work is read then, not before. */
  focused: boolean;
  tags: NoteTag[];
}) {
  const { t, locale } = useI18n();
  // The @ whose menu Escape closed: it stays closed until that @ goes (the field says when).
  const [dismissed, setDismissed] = useState<number | null>(null);
  const [active, setActive] = useState(0);
  const listId = useId();

  // Read once the field is first used, and kept a while: the reader's open work does not change by the second.
  const taggable = useQuery({
    queryKey: TAGGABLE_KEY,
    queryFn: () => api.get<TaggableOptions>('/notes/taggable'),
    enabled: focused,
    staleTime: 30_000,
  });

  const query = trigger?.query ?? null;
  const full = tags.length >= MAX_TAGS;
  const everything = useMemo(
    () => (taggable.data ? [...taggable.data.tasks, ...taggable.data.qTasks, ...taggable.data.tickets] : []),
    [taggable.data],
  );

  const matches = useMemo(() => {
    if (query === null) return [];
    const words = fold(query).split(/\s+/).filter(Boolean);
    return everything
      .filter((option) => !tags.some((tag) => sameTag(tag, option)))
      .filter((option) => {
        if (words.length === 0) return true;
        const haystack = fold(
          [option.number, option.title, t(tagKindKey(option)), enumLabel(option.status, locale)].join(' '),
        );
        return words.every((word) => haystack.includes(word));
      })
      .slice(0, SHOWN);
  }, [query, everything, tags, t, locale]);

  // Closed once a space follows a query nothing matches: the @ was only a character after all.
  const open =
    trigger !== null &&
    trigger.at !== dismissed &&
    !(taggable.data && matches.length === 0 && /\s$/.test(trigger.query));
  const index = Math.min(active, Math.max(0, matches.length - 1));
  const highlighted = open && !full ? matches[index] : undefined;
  const optionId = (option: NoteTag) => `${listId}-${option.kind}-${option.id}`;

  useEffect(() => setActive(0), [query]);
  // Each @ reads the list again unless it was read moments ago: a task given to the reader a minute
  // after they opened the page is offered at once, not when the cache runs out.
  const asking = trigger !== null;
  useEffect(() => {
    if (asking && taggable.dataUpdatedAt > 0 && Date.now() - taggable.dataUpdatedAt > FRESH_FOR) void taggable.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asking]);
  useEffect(() => {
    if (highlighted) document.getElementById(optionId(highlighted))?.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlighted?.kind, highlighted?.id]);

  /**
   * A key, while the menu is open: ↑ ↓ move, Enter or Tab picks, Escape closes
   * it. True when the key was the menu's — the field then does nothing more.
   */
  const onKey = (key: string, pick: (option: NoteTag) => void): boolean => {
    if (!open || !trigger) return false;
    if (key === 'ArrowDown' && matches.length) {
      setActive(Math.min(index + 1, matches.length - 1));
      return true;
    }
    if (key === 'ArrowUp' && matches.length) {
      setActive(Math.max(index - 1, 0));
      return true;
    }
    if ((key === 'Enter' || key === 'Tab') && highlighted) {
      pick(highlighted);
      return true;
    }
    if (key === 'Escape') {
      setDismissed(trigger.at);
      return true;
    }
    return false;
  };

  return {
    open,
    full,
    matches,
    everything,
    highlighted,
    listId,
    optionId,
    taggable,
    dismissed,
    setDismissed,
    setActive,
    onKey,
    /** What the field carries for a screen reader: a combobox whose list is the menu. */
    aria: {
      role: 'combobox',
      'aria-expanded': open,
      'aria-controls': open ? listId : undefined,
      'aria-autocomplete': 'list' as const,
      'aria-activedescendant': highlighted ? optionId(highlighted) : undefined,
    },
  };
}

export type TagMenuState = ReturnType<typeof useTagMenu>;

/** The @ menu itself, under its field: the reader's open work, tasks then Q Tasks then tickets. */
export function TagMenu({ menu, onPick }: { menu: TagMenuState; onPick: (option: NoteTag) => void }) {
  const { t, locale } = useI18n();
  if (!menu.open) return null;
  const { full, taggable, matches, everything, highlighted } = menu;
  // Group headings where the kind changes: tasks, then Q Tasks, then tickets.
  let lastKind: string | null = null;
  return (
    <ul
      id={menu.listId}
      role="listbox"
      aria-label={t('notes.tag.button')}
      className="absolute inset-x-0 top-full z-dropdown mt-1 max-h-72 min-w-[16rem] overflow-y-auto rounded-md surface py-1 shadow-lg"
    >
      {full ? (
        <li className="px-3 py-2.5 text-body-sm text-muted">{t('notes.tag.max', { n: MAX_TAGS })}</li>
      ) : taggable.isLoading ? (
        <li className="px-3 py-2.5 text-body-sm text-muted">{t('notes.tag.loading')}</li>
      ) : taggable.error ? (
        <li className="px-3 py-2.5 text-body-sm text-error-fg dark:text-error-dark">{(taggable.error as Error).message}</li>
      ) : matches.length === 0 ? (
        <li className="px-3 py-2.5 text-body-sm text-muted">
          {everything.length === 0 ? t('notes.tag.none') : t('notes.tag.noMatch')}
        </li>
      ) : (
        matches.map((option, i) => {
          const heading = option.kind !== lastKind;
          lastKind = option.kind;
          const details = [
            t(tagKindKey(option)),
            enumLabel(option.status, locale),
            option.priority === 'URGENT' ? enumLabel('URGENT', locale) : null,
            option.dueAt ? t('notes.tag.due', { date: formatDate(option.dueAt, locale) }) : null,
          ].filter(Boolean);
          return (
            <Fragment key={`${option.kind}:${option.id}`}>
              {heading && (
                <li role="presentation" className="px-3 pb-1 pt-2 text-overline uppercase text-[var(--text-tertiary)]">
                  {t(`notes.tag.group.${option.kind}`)}
                </li>
              )}
              <li
                id={menu.optionId(option)}
                role="option"
                aria-selected={option === highlighted}
                // Before the field's blur, so the menu is still there for the click.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onPick(option)}
                onMouseEnter={() => menu.setActive(i)}
                className={clsx(
                  'flex cursor-pointer items-center gap-2.5 px-3 py-2',
                  option === highlighted ? 'bg-primary/10' : 'hover:bg-[var(--surface-sunken)]',
                )}
              >
                <TagIcon tag={option} className="h-4 w-4 text-faint" />
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-1.5 text-body-sm font-medium text-[var(--text-primary)]">
                    {option.number && (
                      <span className="shrink-0 font-mono text-caption text-muted" dir="ltr">
                        {option.number}
                      </span>
                    )}
                    <bdi className="truncate">{option.title}</bdi>
                  </span>
                  <span className="block truncate text-caption text-muted">{details.join(' · ')}</span>
                </span>
              </li>
            </Fragment>
          );
        })
      )}
    </ul>
  );
}

/**
 * The words of a to-do line, where @ tags a task, a Q Task or a ticket given
 * to the reader. Typing @ opens their open work (`useTagMenu`); picking takes
 * the @ and the query out of the words and adds the tag, which the page shows
 * as a chip. The @ button does the same for whoever does not type it.
 *
 * With the menu closed, Enter saves a one-line field, Ctrl+Enter a multi-line
 * one; Escape gives up an edit. A note is written in the note editor
 * (NoteEditor.tsx), which has formatting and the same menu.
 */
export function TagField({
  value,
  onChange,
  tags,
  onTagsChange,
  multiline = false,
  rows = 4,
  placeholder,
  label,
  maxLength,
  onSubmit,
  onCancel,
  autoFocus,
  disabled,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  tags: NoteTag[];
  onTagsChange: (tags: NoteTag[]) => void;
  multiline?: boolean;
  rows?: number;
  placeholder: string;
  label: string;
  maxLength: number;
  onSubmit?: () => void;
  onCancel?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useI18n();
  const field = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState<number | null>(null);
  const [focused, setFocused] = useState(false);

  const trigger = focused && !disabled ? findTrigger(value, caret) : null;
  const menu = useTagMenu({ trigger, focused, tags });
  const { full, setDismissed } = menu;

  const placeCaret = (position: number) => {
    requestAnimationFrame(() => {
      const element = field.current;
      if (!element) return;
      element.focus();
      element.setSelectionRange(position, position);
      setCaret(position);
    });
  };

  const pick = (option: NoteTag) => {
    if (!trigger || full) return;
    const before = value.slice(0, trigger.at);
    let after = value.slice(trigger.at + 1 + trigger.query.length);
    // One space between the words either side, not two.
    if ((before === '' || /\s$/.test(before)) && /^[ \t]/.test(after)) after = after.slice(1);
    onChange(before + after);
    onTagsChange([...tags, option]);
    menu.setActive(0);
    placeCaret(before.length);
  };

  /** The @ button: an @ at the caret, as if typed, which opens the menu. */
  const typeAt = () => {
    const element = field.current;
    if (!element || full) return;
    const start = element.selectionStart ?? value.length;
    const end = element.selectionEnd ?? start;
    const before = value.slice(0, start);
    const insert = before && !/\s$/.test(before) ? ' @' : '@';
    const next = before + insert + value.slice(end);
    if (next.length > maxLength) return;
    onChange(next);
    setDismissed(null);
    setFocused(true);
    placeCaret(start + insert.length);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // An input method finishing a word (Arabic keyboards) presses Enter too.
    if (e.nativeEvent.isComposing) return;
    if (menu.onKey(e.key, pick)) {
      e.preventDefault();
      // Escape closes the menu, not the dialog or the edit around it.
      e.stopPropagation();
      return;
    }
    if (e.key === 'Enter' && (!multiline || e.ctrlKey || e.metaKey)) {
      // Never the form around it: saving is this field's own.
      e.preventDefault();
      onSubmit?.();
    } else if (e.key === 'Escape' && onCancel) {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Backspace' && value === '' && tags.length > 0) {
      // As in the people picker: Backspace in an empty field takes off the last tag.
      onTagsChange(tags.slice(0, -1));
    }
  };

  const common = {
    ref: field,
    value,
    disabled,
    autoFocus,
    maxLength,
    placeholder,
    // Typed words take their own direction; an empty field the page's, so an Arabic placeholder is not cut at its start.
    dir: value ? ('auto' as const) : undefined,
    'aria-label': label,
    ...menu.aria,
    onChange: (e: { target: { value: string; selectionStart: number | null } }) => {
      const next = e.target.value;
      if (menu.dismissed !== null && next[menu.dismissed] !== '@') setDismissed(null);
      onChange(next);
      setCaret(e.target.selectionStart);
    },
    onSelect: (e: { currentTarget: { selectionStart: number | null } }) => setCaret(e.currentTarget.selectionStart),
    onFocus: (e: { currentTarget: { selectionStart: number | null } }) => {
      setFocused(true);
      setCaret(e.currentTarget.selectionStart);
    },
    // Late enough that a click in the menu lands first — and not at all if the focus came straight back.
    onBlur: () =>
      setTimeout(() => {
        if (document.activeElement !== field.current) setFocused(false);
      }, 150),
    onKeyDown,
    className: clsx(
      'w-full min-w-0 bg-transparent px-3 text-body text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] dark:placeholder:text-[var(--text-secondary)]',
      // The box shows focus, not the field inside it: two rings would nest.
      'focus:outline-none focus-visible:shadow-none disabled:cursor-not-allowed',
      multiline ? 'resize-y py-2' : 'h-10',
    ),
  };

  return (
    <div className={clsx('relative min-w-0', className)}>
      <div
        className={clsx(
          'flex items-start rounded-md border bg-[var(--surface-raised)] transition-colors duration-fast hairline',
          'focus-within:border-primary focus-within:ring-1 focus-within:ring-primary',
          disabled && 'opacity-60',
        )}
      >
        {multiline ? <textarea rows={rows} {...common} /> : <input type="text" {...common} />}
        <button
          type="button"
          // Keeps the caret where it was, for the @ to go there.
          onMouseDown={(e) => e.preventDefault()}
          onClick={typeAt}
          disabled={disabled || full}
          title={full ? t('notes.tag.max', { n: MAX_TAGS }) : t('notes.tag.button')}
          aria-label={t('notes.tag.button')}
          className="m-1 grid h-8 w-8 shrink-0 place-items-center rounded-md text-faint transition-colors duration-fast hover:bg-[var(--surface-sunken)] hover:text-primary-600 dark:hover:text-primary-400 disabled:opacity-50"
        >
          <AtSign className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <TagMenu menu={menu} onPick={pick} />
    </div>
  );
}
